document.addEventListener("alpine:init", () => {
  Alpine.data("VariantUI", () => ({
    variants: [],
    selected: [],
    currentVariant: null,
    qty: 1,
    loading: false,

    // Calculator integration
    calculatorVariant: null,
    calculatorQty: null,
    isCalculatorMode: false,

    scentValues: [],
    scentImages: [],

    bisEmail: "",
    bisSuccess: false,
    klaviyoApiKey: null,

    init() {
      // Get Klaviyo API key from section settings
      if (this.$refs.klaviyoKey) {
        this.klaviyoApiKey = this.$refs.klaviyoKey.textContent.trim();
      }

      // Check if calculator data is available
      this.checkCalculatorData();

      // Variants base
      this.variants = JSON.parse(this.$refs.variants.textContent || "[]");

      if (this.$refs.scentMeta) {
        const s = JSON.parse(this.$refs.scentMeta.textContent || "{}");
        this.scentValues = s.values || [];
        this.scentImages = s.images || [];
      }

      if (this.variants.length) {
        if (this.isCalculatorMode && this.calculatorVariant) {
          // Use calculator-selected variant
          this.setCalculatorVariant();
        } else {
          // Regular mode - use first available variant
          const v = this.variants.find((v) => v.available) || this.variants[0];
          this.selected = [v.option1, v.option2, v.option3].filter(Boolean);
          this.updateVariant(false);
        }
      }

      // Listen for calculator updates
      this.setupCalculatorListener();
    },

    checkCalculatorData() {
      if (
        window.modernUX &&
        typeof window.modernUX.getProductVariantsByCan === "function"
      ) {
        const calculatorResult = window.modernUX.getProductVariantsByCan();
        if (
          calculatorResult &&
          calculatorResult.id &&
          calculatorResult.quantity
        ) {
          this.calculatorVariant = calculatorResult.id;
          this.calculatorQty = calculatorResult.quantity;
          this.isCalculatorMode = true;
          this.qty = calculatorResult.quantity;
        }
      }
    },

    setupCalculatorListener() {
      document.addEventListener("calculatorUpdated", (event) => {
        if (event.detail && event.detail.variantId && event.detail.quantity) {
          this.calculatorVariant = event.detail.variantId;
          this.calculatorQty = event.detail.quantity;
          this.isCalculatorMode = true;
          this.qty = event.detail.quantity;
          this.setCalculatorVariant();
        }
      });
    },

    setCalculatorVariant() {
      const variant = this.variants.find(
        (v) => v.id === this.calculatorVariant,
      );
      if (variant) {
        this.selected = [
          variant.option1,
          variant.option2,
          variant.option3,
        ].filter(Boolean);
        this.currentVariant = variant;
        this.qty = this.calculatorQty;
      }
    },

    // Image helpers
    scentImg(value) {
      const i = this.scentValues.indexOf(value);
      if (i < 0) return null;
      return this.scentImages[i] || null;
    },

    isPossible(i, value) {
      const trial = [...this.selected];
      trial[i] = value;
      return this.variants.some(
        (v) =>
          trial.every((val, idx) => !val || v[`option${idx + 1}`] === val) &&
          v.available,
      );
    },

    // Selection
    select(i, value) {
      if (this.isCalculatorMode) {
        this.isCalculatorMode = false;
      }

      this.selected[i] = value;
      this.updateVariant(true);
    },

    // Refresh URL y currentVariant
    updateVariant(pushUrl = true) {
      this.currentVariant =
        this.variants.find((v) =>
          this.selected.every((val, idx) => v[`option${idx + 1}`] === val),
        ) || null;

      if (this.currentVariant && pushUrl) {
        const sp = new URLSearchParams(location.search);
        sp.set("variant", this.currentVariant.id);
        history.replaceState({}, "", `${location.pathname}?${sp}`);
      }
    },

    // Get display quantity
    getDisplayQty() {
      return this.isCalculatorMode ? this.calculatorQty : this.qty;
    },

    // Price
    money(cents) {
      return ((cents || 0) / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    getTotalPrice() {
      if (!this.currentVariant) return 0;
      const quantity = this.isCalculatorMode ? this.calculatorQty : this.qty;
      return this.currentVariant.price * quantity;
    },

    // Add to cart — with try/catch so loading never gets stuck
    async addToCart() {
      if (!this.currentVariant) return;
      this.loading = true;

      try {
        const quantity = this.isCalculatorMode ? this.calculatorQty : this.qty;

        // Check if Appstle has a selling plan selected
        const sellingPlanInput = document.querySelector('input[name="selling_plan"]:checked');
        const sellingPlanValue = sellingPlanInput ? sellingPlanInput.value : '';

        // Add main product (with selling plan if subscription is selected)
        await Alpine.store("cart").add({
          id: this.currentVariant.id,
          quantity: quantity,
          selling_plan: sellingPlanValue || null,
        });

        // Reset quantity if not in calculator mode
        if (!this.isCalculatorMode) {
          this.qty = 1;
        }
      } catch (err) {
        console.error('Add to cart error:', err);
      } finally {
        // Always reset loading — even if there's an error
        this.loading = false;
      }
    },

    async submitBISForm(event) {
      event.preventDefault();

      if (!this.currentVariant) return;

      const email = this.$refs.bisEmail.value;

      if (!email) {
        alert("Please enter your email.");
        return;
      }

      const payload = {
        data: {
          type: "back-in-stock-subscription",
          attributes: {
            channels: ["EMAIL"],
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email: email,
                },
              },
            },
          },
          relationships: {
            variant: {
              data: {
                type: "catalog-variant",
                id: `$shopify:::$default:::${this.currentVariant.id}`,
              },
            },
          },
        },
      };

      try {
        const apiKey = this.klaviyoApiKey || "TUv4Y5";
        const res = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${apiKey}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/vnd.api+json",
              Accept: "application/vnd.api+json",
              Revision: "2025-01-15",
            },
            body: JSON.stringify(payload),
          },
        );

        if (!res.ok) {
          console.error("Klaviyo BIS error:", await res.json());
          alert("There was an error. Try again!");
          return;
        }

        this.bisSuccess = true;
        this.$refs.bisForm.reset();
      } catch (err) {
        console.error("Network error", err);
        alert("Connection error.");
      }
    },
  }));
});
